/**
 * A ponte com a pagina que embute.
 *
 * Vocabulario FECHADO e curto. Nada de `eval`, nada de HTML atravessando, e
 * nenhuma mensagem cujo conteudo vire codigo deste lado: o que chega do host e
 * dado, e todo campo e validado antes de virar estado.
 *
 * Quem manda o quê:
 *
 *   host -> widget:  session · open · close · theme · layout
 *   widget -> host:  ready · resize · unread · drag · closed · fullscreen
 *
 * O `session` traz o `public_tokens` porque o reCAPTCHA do `/public/session`
 * tem de ser resolvido na PAGINA DO HOST: o `hostname` que o Google atesta e o
 * que resolve o tenant na gateway. Resolvido aqui dentro, o hostname seria o
 * do iframe e o lookup cairia no tenant errado.
 */

export type VisitorIdentity = {
  name?: string | null
  email?: string | null
  phone?: string | null
  identification?: string | null
}

export type HostInbound =
  | {
      type: "session"
      publicToken: string
      visitorIdentity?: VisitorIdentity | null
      /** Reabrir a conversa que existir — nunca criar. */
      reabrirApenas?: boolean
      /**
       * Primeira mensagem, enviada em nome da pessoa assim que a conversa conectar.
       * Vem do checkout da venda assistida: o interesse ja escrito, com o codigo.
       */
      initialMessage?: string | null
      /** De onde a pessoa veio (pagina, referrer, utm): origem da conversa nova. */
      origin?: ChatOrigin | null
      /**
       * Codigo do Interesse (INT-...) do carrinho do site. O servidor liga a conversa
       * a ele pela sessao; sem isto, no site de terceiro, so sobrava o codigo escrito
       * dentro da primeira mensagem, que a pessoa pode apagar.
       */
      interestCode?: string | null
    }
  /**
   * Sessao JA resolvida pelo servidor do host.
   *
   * Existe por uma razao concreta, nao por conveniencia: no nosso front o
   * `auth_token` do usuario vive num cookie httpOnly. O browser nao consegue
   * le-lo, e um iframe de outra origem menos ainda — entao a unica forma de a
   * conversa nascer JA identificada e o servidor do host fazer a troca e
   * entregar o resultado.
   *
   * O preco: neste caminho o token da conversa atravessa a ponte e fica ao
   * alcance do JS do host. Aceitavel numa pagina PRIMEIRA-PARTE, que ja tem o
   * cookie de sessao da pessoa. Um site de terceiro nunca usa isto — la o
   * `publicToken` acima e o widget faz a troca sozinho, e o token da conversa
   * nasce e morre dentro do iframe.
   */
  | { type: "session"; session: ResolvedSession; initialMessage?: string | null }
  /**
   * Nao havia conversa para reabrir, e nao era para criar.
   *
   * Existe porque silencio nao e resposta: sem isto o widget ficava com o
   * pedido pendente para sempre, e o clique seguinte era engolido pela trava.
   */
  | { type: "session-none" }
  | { type: "open" }
  | { type: "close" }
  | { type: "theme"; theme: "light" | "dark" }
  | {
      type: "layout"
      above: boolean
      toLeft: boolean
      /**
       * Painel na tela inteira (celular). Sem bolha, sem cantos, sem margem —
       * e a janela passa a ser modal: Esc fecha, como em qualquer dialogo.
       */
      fullscreen: boolean
      /** Aberto por link: a pagina esta escurecida atras do painel. */
      destaque: boolean
      /** O teclado do aparelho esta na tela (quem enxerga isso e o host). */
      teclado: boolean
      /**
       * Mostrar o botao de tela cheia: celular, e nada que ja force a tela
       * cheia. Quem sabe e o host — daqui so se ve a largura do iframe.
       */
      podeAmpliar: boolean
    }
  /**
   * "Quem estava aqui nao esta mais" — logout ou troca de usuario no host, SEM
   * recarregar a pagina. O widget descarta a sessao na hora: recarregar ja
   * matava o token (ele vive na memoria deste iframe), a pagina que fica de pe
   * e que herdava a conversa da pessoa anterior.
   */
  | { type: "identity-reset" }

export type ResolvedSession = {
  socketToken: string
  conversationId: string
  expiresAt?: string
  identity?: { isGuest: boolean; userId: number | null }
  config?: unknown
}

export type WidgetOutbound =
  | { type: "ready" }
  | { type: "resize"; open: boolean }
  | { type: "unread"; count: number }
  | { type: "drag"; phase: "start" | "move" | "end"; dx: number; dy: number }
  | { type: "closed" }
  | { type: "session-refresh"; reabrirApenas?: boolean }
  /** A troca do token publico foi RECUSADA — o host tem de jogar fora o dele. */
  | { type: "session-rejected" }
  /**
   * Pedido de tela cheia (botao do cabecalho). O host decide e responde pelo
   * `layout`; o icone so troca quando a resposta chega.
   */
  | { type: "fullscreen"; on: boolean }

const NAMESPACE = "tecwb-chat"

/** `true` quando o widget nao esta dentro de um iframe (aberto direto). */
export const isStandalone = (): boolean => {
  try {
    return window.self === window.top
  } catch {
    // Cross-origin ao ler `window.top` significa que HA um pai de outra origem.
    return false
  }
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null
}

/** Origem do chat no site: so os campos conhecidos, texto e com tamanho limitado. */
export interface ChatOrigin {
  pageUrl?: string | null
  landingUrl?: string | null
  referrer?: string | null
  utm?: Record<string, string> | null
}

const UTM_CONHECIDOS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "gclid", "fbclid"]

export function parseChatOrigin(value: unknown): ChatOrigin | null {
  if (!value || typeof value !== "object") return null
  const raw = value as Record<string, unknown>
  const url = (v: unknown) => {
    const s = asString(v)
    return s && /^https?:\/\//i.test(s) ? s.slice(0, 1000) : null
  }
  const utmRaw = raw.utm && typeof raw.utm === "object" ? (raw.utm as Record<string, unknown>) : {}
  const utm: Record<string, string> = {}
  for (const chave of UTM_CONHECIDOS) {
    const v = asString(utmRaw[chave])
    if (v) utm[chave] = v.slice(0, 200)
  }
  const origin: ChatOrigin = {
    pageUrl: url(raw.pageUrl),
    landingUrl: url(raw.landingUrl),
    referrer: url(raw.referrer),
    utm: Object.keys(utm).length ? utm : null,
  }
  return origin.pageUrl || origin.landingUrl || origin.referrer || origin.utm ? origin : null
}

function parseVisitorIdentity(value: unknown): VisitorIdentity | null {
  if (!value || typeof value !== "object") return null
  const raw = value as Record<string, unknown>
  const identity: VisitorIdentity = {}
  for (const key of ["name", "email", "phone", "identification"] as const) {
    const text = asString(raw[key])
    if (text) identity[key] = text
  }
  return Object.keys(identity).length ? identity : null
}

/** So entra quando vem (a ponte e um vocabulario fechado). */
function comOrigem(value: unknown): { origin?: ChatOrigin } {
  const origin = parseChatOrigin(value)
  return origin ? { origin } : {}
}

/** Converte o dado cru do `postMessage` numa mensagem conhecida, ou `null`. */
export function parseHostMessage(data: unknown): HostInbound | null {
  if (!data || typeof data !== "object") return null
  const raw = data as Record<string, unknown>
  if (raw.ns !== NAMESPACE) return null

  switch (raw.type) {
    case "session": {
      // So' entra quando vem: o vocabulario da ponte e' fechado, e uma chave a
      // mais em toda sessao seria ruido para quem compara o objeto inteiro.
      const initialMessage = asString(raw.initialMessage)
      const comMensagem = initialMessage ? { initialMessage } : {}
      const resolved = raw.session
      if (resolved && typeof resolved === "object") {
        const r = resolved as Record<string, unknown>
        const socketToken = asString(r.socketToken)
        const conversationId = asString(r.conversationId)
        return socketToken && conversationId
          ? { type: "session", session: r as unknown as ResolvedSession, ...comMensagem }
          : null
      }
      const publicToken = asString(raw.publicToken)
      return publicToken
        ? {
            type: "session",
            publicToken,
            visitorIdentity: parseVisitorIdentity(raw.visitorIdentity),
            reabrirApenas: raw.reabrirApenas === true,
            ...comMensagem,
            ...comOrigem(raw.origin),
            ...(asString(raw.interestCode) ? { interestCode: asString(raw.interestCode) } : {}),
          }
        : null
    }
    case "session-none":
      return { type: "session-none" }
    case "identity-reset":
      return { type: "identity-reset" }
    case "open":
      return { type: "open" }
    case "close":
      return { type: "close" }
    case "theme":
      return raw.theme === "light" || raw.theme === "dark"
        ? { type: "theme", theme: raw.theme }
        : null
    case "layout":
      // Quem decide onde o painel abre e o HOST — so ele enxerga a viewport da
      // pagina. O widget obedece, em vez de recalcular a mesma regra e correr o
      // risco de discordar num caso de borda.
      //
      // `above`/`toLeft` sao obrigatorios; os demais chegam como falso quando
      // faltam — um embed.js antigo em cache no navegador de alguem nao os
      // manda, e a mensagem dele nao pode virar `null` e levar junto a
      // orientacao do painel. Sem `podeAmpliar`, o botao so nao aparece.
      return typeof raw.above === "boolean" && typeof raw.toLeft === "boolean"
        ? {
            type: "layout",
            above: raw.above,
            toLeft: raw.toLeft,
            fullscreen: raw.fullscreen === true,
            destaque: raw.destaque === true,
            teclado: raw.teclado === true,
            podeAmpliar: raw.podeAmpliar === true,
          }
        : null
    default:
      // Vocabulario fechado: o que nao esta na lista nao existe.
      return null
  }
}

export function createBridge(onMessage: (message: HostInbound) => void) {
  // A origem do host nao e conhecida de antemao — o widget roda no site de
  // qualquer cliente. A protecao aqui e o vocabulario fechado acima e o fato de
  // NENHUMA mensagem carregar credencial de saida: o `publicToken` que chega ja
  // e publico, e o token de conversa nasce e morre deste lado, sem nunca
  // atravessar a ponte.
  const handler = (event: MessageEvent) => {
    const parsed = parseHostMessage(event.data)
    if (parsed) onMessage(parsed)
  }

  window.addEventListener("message", handler)

  const post = (message: WidgetOutbound) => {
    if (isStandalone()) return
    window.parent.postMessage({ ns: NAMESPACE, ...message }, "*")
  }

  return {
    post,
    destroy: () => window.removeEventListener("message", handler),
  }
}
